import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAPI } from '@hooks/useAPI'
import { getAchievements } from '@services/api/achievements'
import AchievementCard from '@components/business/AchievementCard'
import Pagination from '@components/ui/Pagination'
import Loading from '@components/ui/Loading'
import Button from '@components/ui/Button'

const Achievements = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // 获取URL参数
  const currentPage = parseInt(searchParams.get('page')) || 1

  // 状态管理
  const [filters, setFilters] = useState({
    page: currentPage,
    limit: 12,
    sort: 'completionDate',
    order: 'desc'
  })

  // API调用
  const { 
    data: achievementsData, 
    loading: achievementsLoading, 
    error: achievementsError,
    refetch: refetchAchievements 
  } = useAPI(() => getAchievements(filters), [filters, i18n.language])

  // 更新URL参数
  const updateURLParams = (newFilters) => {
    const params = new URLSearchParams()
    if (newFilters.page && newFilters.page > 1) {
      params.set('page', newFilters.page)
    }
    setSearchParams(params)
  }

  // 处理分页变化
  const handlePageChange = (page) => {
    const newFilters = { ...filters, page }
    setFilters(newFilters)
    updateURLParams(newFilters)
  }

  // 处理成果卡片点击
  const handleAchievementClick = (achievementId) => {
    navigate(`/achievements/${achievementId}`)
  }

  const achievements = achievementsData?.records || []
  const totalPages = achievementsData?.pages || 1
  const totalItems = achievementsData?.total || 0

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-12">
        <div className="container section-padding text-center">
          <h1 className="text-4xl font-bold mb-4">
            {t('achievements.title', '成果展示中心')}
          </h1>
          <p className="text-xl text-primary-100">
            {t('achievements.subtitle', '版权合作与文化交流的丰硕成果')}
          </p>
        </div>
      </div>

      <div className="container section-padding py-8">

        {/* 成果列表 */}
        {achievementsLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loading />
          </div>
        ) : achievementsError ? (
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-lg font-medium">{t('achievements.states.loadFailed')}</p>
              <p className="text-sm text-gray-500 mt-2">{achievementsError}</p>
            </div>
            <Button onClick={() => refetchAchievements()}>
              {t('achievements.states.retry')}
            </Button>
          </div>
        ) : achievements.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-4">
              <div className="text-6xl mb-4">📋</div>
              <p className="text-lg font-medium">{t('achievements.states.noResults')}</p>
              <p className="text-sm mt-2">{t('achievements.states.noResultsHint')}</p>
            </div>
          </div>
        ) : (
          <>


            {/* 成果网格 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {achievements.map((achievement) => (
                <AchievementCard
                  key={achievement.resultId}
                  achievement={achievement}
                  onClick={() => handleAchievementClick(achievement.resultId)}
                />
              ))}
            </div>

            {/* 分页导航 */}
            {totalPages > 1 && (
              <div className="flex justify-center">
                <Pagination
                  currentPage={filters.page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Achievements 